import { isRetryableHttpStatus, isRetryableNetworkError } from "@/lib/network/networkMessages";

export type FetchWithRetryOptions = RequestInit & {
  /** Per-attempt timeout in ms (uses AbortController). Default 30_000. */
  timeoutMs?: number;
  /** Max attempts including the first. Default 3. */
  maxAttempts?: number;
  /** Base delay before retry in ms; doubles each attempt. Default 800. */
  retryDelayMs?: number;
  /** External signal — abort cancels all attempts. */
  signal?: AbortSignal;
  /** When true, only retry on network errors and 5xx/408/429. Default true. */
  retryOnHttpError?: boolean;
};

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const t = window.setTimeout(resolve, ms);
    const onAbort = () => {
      window.clearTimeout(t);
      reject(new DOMException("Aborted", "AbortError"));
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

function mergeSignals(a?: AbortSignal, b?: AbortSignal): AbortSignal | undefined {
  if (!a) return b;
  if (!b) return a;
  const ctrl = new AbortController();
  const abort = () => ctrl.abort();
  if (a.aborted || b.aborted) {
    abort();
    return ctrl.signal;
  }
  a.addEventListener("abort", abort, { once: true });
  b.addEventListener("abort", abort, { once: true });
  return ctrl.signal;
}

/**
 * fetch with timeout, exponential backoff retry, and optional external abort.
 * Safe for idempotent GETs and POSTs guarded by caller-side in-flight locks.
 */
export async function fetchWithRetry(
  input: RequestInfo | URL,
  options: FetchWithRetryOptions = {},
): Promise<Response> {
  const {
    timeoutMs = 30_000,
    maxAttempts = 3,
    retryDelayMs = 800,
    signal: externalSignal,
    retryOnHttpError = true,
    ...init
  } = options;

  let lastError: unknown;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const attemptCtrl = new AbortController();
    const timeoutId = window.setTimeout(() => attemptCtrl.abort(), timeoutMs);
    const signal = mergeSignals(externalSignal, attemptCtrl.signal);

    try {
      const res = await fetch(input, { ...init, signal });
      window.clearTimeout(timeoutId);

      if (retryOnHttpError && !res.ok && isRetryableHttpStatus(res.status) && attempt < maxAttempts - 1) {
        await sleep(retryDelayMs * 2 ** attempt, externalSignal);
        continue;
      }
      return res;
    } catch (e) {
      window.clearTimeout(timeoutId);
      lastError = e;
      if (!isRetryableNetworkError(e) || attempt >= maxAttempts - 1) throw e;
      await sleep(retryDelayMs * 2 ** attempt, externalSignal);
    }
  }
  throw lastError ?? new Error("Request failed");
}
